/**
 * Store Health — Stage 3 deliverable.
 *
 * Two-pane layout:
 *   - Left: searchable list of stores with online dots + counters.
 *   - Right: roster grid for the selected store (riders / packers /
 *     billers / managers) with status pills.
 *
 * Powered entirely by the existing react_api endpoints
 * `/api/stores/online/` and `/api/stores/online/{id}/`. No new BI
 * endpoint is needed — see workspace rule §1.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Bike,
  CheckCircle2,
  CircleDashed,
  Headset,
  PackageCheck,
  Search,
  ShieldCheck,
  Store as StoreIcon,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Input } from '@/components/ui/Input';
import {
  useStoresList,
  useStoreDetail,
  type StoreLite,
  type StoreStaffRow,
} from '@/api/legacy/stores';
import { cn } from '@/lib/cn';
import { openRiderOrders } from '@/state/riderOrdersDialog';

export function StoreHealthPage() {
  const stores = useStoresList();
  const [search, setSearch] = useState('');
  const [storeId, setStoreId] = useState<number | null>(null);

  const list = stores.data ?? [];
  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((s) => s.name.toLowerCase().includes(q));
  }, [list, search]);

  // Auto-pick the first store on first load so the right pane isn't empty.
  useEffect(() => {
    if (storeId == null && filtered.length > 0) setStoreId(filtered[0].id);
  }, [filtered, storeId]);

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Store Health"
        description="A live roster grid for every store you can access — at a glance you see who is on shift right now."
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="overflow-hidden">
          <div className="border-b border-border px-3 py-2.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find a store…"
                className="h-8 pl-7 text-xs"
              />
            </div>
          </div>
          <ul className="max-h-[640px] divide-y divide-border overflow-y-auto">
            {stores.isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="px-3 py-2"><Skeleton className="h-6 w-full" /></li>
                ))
              : filtered.map((s) => (
                  <StoreRow key={s.id} store={s} active={s.id === storeId} onSelect={() => setStoreId(s.id)} />
                ))}
            {!stores.isLoading && filtered.length === 0 ? (
              <li className="px-4 py-6">
                <EmptyState title="No stores match" />
              </li>
            ) : null}
          </ul>
        </Card>

        <StoreDetailPanel storeId={storeId} />
      </div>
    </>
  );
}

function StoreRow({ store, active, onSelect }: { store: StoreLite; active: boolean; onSelect: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'press flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm focus-ring',
          active ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className={cn(
              'inline-block h-2 w-2 shrink-0 rounded-full',
              store.is_online ? 'bg-success' : 'bg-muted-foreground/40',
            )}
          />
          <span className="truncate">{store.name}</span>
        </span>
        <Badge tone={store.is_online ? 'success' : 'neutral'} className="text-[10px] uppercase">
          {store.is_online ? 'live' : 'down'}
        </Badge>
      </button>
    </li>
  );
}

function StoreDetailPanel({ storeId }: { storeId: number | null }) {
  const detail = useStoreDetail(storeId);

  if (storeId == null) {
    return (
      <Card>
        <CardBody>
          <EmptyState title="Pick a store" description="Select any store on the left to inspect its roster." />
        </CardBody>
      </Card>
    );
  }

  if (detail.isError) return <Card><CardBody><ErrorState onRetry={() => void detail.refetch()} /></CardBody></Card>;
  if (detail.isLoading || !detail.data) return <Card><CardBody><Skeleton className="h-64 w-full" /></CardBody></Card>;

  const d = detail.data;
  const counts = {
    riders: (d.rider_status_list ?? []).length,
    packers: (d.packer_status_list ?? []).length,
    billers: (d.biller_status_list ?? []).length,
    managers: (d.manager_status_list ?? []).length,
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="inline-flex items-center gap-2 text-base text-foreground">
              <StoreIcon className="h-4 w-4 text-muted-foreground" />
              {d.name}
              <Badge tone={d.is_online ? 'success' : 'danger'} className="capitalize">
                {d.is_online ? 'online' : 'offline'}
              </Badge>
            </CardTitle>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {d.in_progress_orders != null ? `${d.in_progress_orders} in flight` : null}
              {d.pending_orders != null ? ` · ${d.pending_orders} pending` : null}
              {d.cancelled_today != null ? ` · ${d.cancelled_today} cancelled today` : null}
            </p>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Stat icon={<Bike className="h-3 w-3" />} label="riders" value={counts.riders} />
            <Stat icon={<PackageCheck className="h-3 w-3" />} label="packers" value={counts.packers} />
            <Stat icon={<Headset className="h-3 w-3" />} label="billers" value={counts.billers} />
            <Stat icon={<ShieldCheck className="h-3 w-3" />} label="managers" value={counts.managers} />
          </div>
        </CardHeader>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <RosterCard
          title="Riders"
          icon={<Bike className="h-4 w-4" />}
          rows={d.rider_status_list ?? []}
          onRiderNameClick={
            storeId != null
              ? (row) =>
                  openRiderOrders({
                    riderId: row.id,
                    riderName: row.name,
                    storeId,
                  })
              : undefined
          }
        />
        <RosterCard title="Packers" icon={<PackageCheck className="h-4 w-4" />} rows={d.packer_status_list ?? []} />
        <RosterCard title="Billers" icon={<Headset className="h-4 w-4" />} rows={d.biller_status_list ?? []} />
        <RosterCard title="Managers" icon={<ShieldCheck className="h-4 w-4" />} rows={d.manager_status_list ?? []} />
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5">
      {icon}
      <span className="kpi-num font-medium text-foreground">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

function RosterCard({
  title,
  icon,
  rows,
  onRiderNameClick,
}: {
  title: string;
  icon: React.ReactNode;
  rows: StoreStaffRow[];
  /** When set (riders card only), the rider name opens delivery history. */
  onRiderNameClick?: (row: StoreStaffRow) => void;
}) {
  const online = rows.filter((r) => r.is_online).length;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <Badge tone={online > 0 ? 'success' : 'neutral'}>
          {online}/{rows.length} online
        </Badge>
      </CardHeader>
      <CardBody className="px-0 pb-2">
        {rows.length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState
              icon={<Users className="h-4 w-4" />}
              title={`No ${title.toLowerCase()} on file`}
            />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-2 px-5 py-2 text-sm">
                <span className="inline-flex min-w-0 items-center gap-2">
                  {r.is_online ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
                  ) : (
                    <CircleDashed className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden />
                  )}
                  {onRiderNameClick ? (
                    <button
                      type="button"
                      onClick={() => onRiderNameClick(r)}
                      className="truncate text-left font-medium text-foreground underline-offset-2 hover:text-info hover:underline focus-ring rounded-sm"
                    >
                      {r.name}
                    </button>
                  ) : (
                    <span className="truncate font-medium">{r.name}</span>
                  )}
                </span>
                <span className="text-[11px] text-muted-foreground">{r.status}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
